import { useContext, useRef, useCallback } from 'react';
import { GRANTS } from '../../other/constants/PrivilegeTypes.jsx';
import { MessageDialog, StringHelper } from "@bryntum/schedulerpro";
import { toast } from "react-toastify";
import {
  getFormattedBoardData,
  serverErrorMsg,
  showConfirmationDialog,
  showToast
} from "../../other/utils/utils.jsx";
import {
  importBoardApi,
  scheduleBoardDelete,
  scheduleCancelBoardDelete,
  leaveBoard,
  updateWFBoardActiveState
} from "../../network/network.jsx";
import BryntumContext from '../context/bryntumContext.jsx';
import { EDITOR_KEYS } from '../../other/constants/EditorKeys.jsx';
import { shouldShowAsyncDialog } from '../../bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup.jsx';
import AuthContext from '../context/authContext.jsx';
import { formatBoardData } from './useBoardData.jsx';

/**
 * Determines menu item permissions based on user role and board state
 * Matches the previous Bryntum implementation logic
 * @param {Object} board - The board object
 * @param {Object} user - The current user object
 * @returns {Object} Permission flags for each menu item
 */
const getMenuPermissions = (board, user) => {
  if (!board || !user) {
    return {
      canRename: false,
      canDuplicate: false,
      canDelete: false,
      canCancelDelete: false,
      canLeave: true, // Always allow leaving
      showDelete: false,
      showCancelDelete: false
    };
  }

  const adminsEmail = board.adminEmails?.find(({ email }) => email.includes(user.email));
  return {
    canRename: board.roles === GRANTS.grant_one,
    canDuplicate: !board.deleteDate || !adminsEmail,
    canDelete:  !board?.isOwner,
    canCancelDelete: !board?.isOwner,
    canLeave: true, // Always allow leaving
    showDelete: !board.deleteDate,
    showCancelDelete: board.deleteDate,
  };
};

export const useBoardCardActions = (board) => {
  const { boardManager, editorHandler, selectedBoard, viewManager } = useContext(BryntumContext);
  const { user } = useContext(AuthContext);
  const inputRef = useRef(null);

  // Get menu permissions for the current board and user
  const menuPermissions = getMenuPermissions(board, user);

  // Safe MessageDialog wrapper to prevent infinite loops
  const safeMessageDialog = useCallback(async (config) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          MessageDialog.prompt(config).then(resolve).catch(resolve);
        } catch (error) {
          console.warn('MessageDialog error:', error);
          resolve({ button: null, text: '' });
        }
      }, 100);
    });
  }, []);

  // If no board is provided, only return board creation functions
  if (!board) {
    const handleAddBoard = async (event) => {
      if (!user?.can(GRANTS.sandbox_manipulateBoard)) return;

      if (event.shiftKey) {
        return handleImportBoard();
      }

      const result = await safeMessageDialog({
        title: "Create a new Swivu Board",
        message: "Give your new Swivu Board a unique name.",
        textField: { required: true },
        okButton: { text: "Create New Board" },
        cancelButton: { text: "Cancel" }
      });

      if (result.button === MessageDialog.okButton && result.text?.trim()) {
        try {
          // Add board to store (this triggers network call and store events)
          const [createdBoard] = boardManager.boardNameStore.add({ name: result.text.trim() });

          // Listen for store changes to handle the board addition
          const detacher = boardManager.boardNameStore.on({
            idChange() {
              showToast({ msg: "Board added successfully!", type: "success" });
              boardManager.changeSelectedBoard(formatBoardData(createdBoard));
              detacher();
            },
            swivuAddBoardFailed() {
              showToast({ msg: serverErrorMsg(), type: "error" });
              detacher();
            },
            once: true
          });
        } catch (error) {
          showToast({ msg: serverErrorMsg(), type: "error" });
        }
      }
    };

    const handleImportBoard = () => {
      if (user?.can(GRANTS.sandbox_manipulateBoard)) {
        inputRef.current?.click();
      }
    };

    const handleOnFileUpload = async (e) => {
      const { target: { files: [file] } } = e;
      e.target.value = "";

      const result = await safeMessageDialog({
        title: "Please enter imported board name",
        message: "Enter board name",
        textField: { required: true },
        okButton: { text: "Import" },
        cancelButton: { text: "Cancel" }
      });

      if (result.button !== MessageDialog.okButton || !result.text?.trim()) return;

      const fileReader = new FileReader();
      fileReader.readAsText(file, "UTF-8");
      fileReader.onload = ({ target: { result: fileResult } }) => {
        const newBoardData = StringHelper.safeJsonParse(fileResult);
        if (newBoardData) {
          if (newBoardData.Board && result.text.trim()) {
            newBoardData.Board[0].display_name = result.text.trim();
          }

          const showDialog = shouldShowAsyncDialog('BOARD_IMPORT');
          showDialog && editorHandler.openEditor(EDITOR_KEYS.asyncTaskMessageEditor, null, {record: "BOARD_IMPORT"}, { useExisting: true, by: "recordType" });

          // Make the actual network call
          importBoardApi(newBoardData).catch((err) => {
            if (err.response) showToast({ msg: serverErrorMsg(), type: "error" });
          });
        }
      };
    };

    return {
      handleAddBoard,
      handleImportBoard,
      handleOnFileUpload,
      inputRef
    };
  }

  // Board-specific actions
  const handleOpenWorkspace = () => {
    if (!board) return;

    const canViewBoard = board.grants?.includes(GRANTS.grant_four);
    if (!canViewBoard) {
      console.warn('Access denied to board:', board.name);
      return;
    }

    // Check if the same board is already selected
    if (selectedBoard && selectedBoard.id === board.id) {
      // Same board is already loaded, just navigate to it without reloading
      viewManager.goToPreviousView();
      return;
    }
    // Different board selected, load it
    boardManager.changeSelectedBoard(board);
  };

  const handleRenameBoard = async () => {
    const result = await safeMessageDialog({
      title: `Rename Board`,
      message: `Rename Your ${board.name} Board`,
      textField: { required: true, value: board.name },
      okButton: { text: "Rename Board" },
      cancelButton: { text: "Cancel" }
    });

    if (result.button === MessageDialog.okButton && result.text?.trim() && result.text.trim() !== board.name) {
      try {
        // Find the record in the store and update it directly
        const record = boardManager.boardNameStore.getById(board.id);
        if (record) {
          record.name = result.text.trim();
          // Trigger store update event
          boardManager.boardNameStore.trigger('update', { record });
        }
        toast({ msg: "Board renamed successfully!" });
      } catch (error) {
        toast({ msg: "Error renaming board", type: "error" });
      }
    }
  };

  const handleDuplicateBoard = () => {
    editorHandler.openEditor(EDITOR_KEYS.duplicateBoardEditor, board, {
      record: board,
      context: {
        boardStore: boardManager.boardNameStore,
        boardManager,
        onComplete: () => {},
        editorHandler
      }
    });
  };

  const handleDeleteBoard = async () => {
    const confirmed = await showConfirmationDialog({
      title: `Delete Board`,
      message: `<div>Are you sure you want to permanently delete the board named "${board.name}"? </br> All board admins will be notified by email.<br/> Boards marked for deletion are shown in red.<br/> You have 30 days to cancel the delete request.<br/> Click 3 dot cell of selected board and select "Cancel Delete".</div>`,
      cancelBtnText: { color: "b-blue", cls: "b-raised order-1", text: "Cancel" },
      confirmBtnText: { cls: "bg-[#f6f6f6] text-[#d1d1d1] dark:bg-[#2e2e32] dark:border-1 border-solid border-[#eaeaea] dark:border-[#3e3e41]", text: "Permanently Delete", },
      isCloseByHeader: true,
    });

    if (confirmed) {
      try {
        // Make the actual network call
        const response = await scheduleBoardDelete(board.slug);
        const updatedBoardData = getFormattedBoardData(response.data);

        // Update the store with the response data
        boardManager.boardNameStore.applyChangeset({
          updated: [{ id: board.id, ...updatedBoardData }]
        });
        showToast({ msg: "Board Schedule Deleted Successfully!", type: "success" });
      } catch (error) {
        showToast({ msg: serverErrorMsg() });
      }
    }
  };

  const handleCancelDelete = async () => {
    try {
      // Make the actual network call
      const response = await scheduleCancelBoardDelete(board.slug);
      const updatedBoardData = getFormattedBoardData(response.data);

      // Update the store with the response data
      boardManager.boardNameStore.applyChangeset({
        updated: [{ id: board.id, ...updatedBoardData }]
      });
      showToast({ msg: "Board Schedule Delete Cancelled Successfully!", type: "success" });
    } catch (error) {
      showToast({ msg: serverErrorMsg() });
    }
  };

  const handleLeaveBoard = async () => {
    const confirmed = await showConfirmationDialog({
      title: `Confirm Leaving the Board: ${board.name}`,
      message: "Leaving this board will revoke your access permanently. Are you sure you want to proceed?",
      confirmBtnText: "Leave Board",
      cancelBtnText: "Cancel"
    });

    if (confirmed) {
      try {
        // Make the actual network call
        const response = await leaveBoard(board.slug);

        // Remove the board from the store
        boardManager.boardNameStore.applyChangeset({ removed: [{ id: board.id }] });
        showToast({ msg: `Left board: ${response.data.display_name} successfully`, type: "success" });
      } catch (e) {
        MessageDialog.alert({
          title: `Cannot Leave Board: ${board.name}`,
          message: `<div class="max-w-[350px]">${e?.response?.data}</div>` || serverErrorMsg()
        });
      }
    }
  };

  const handleMenuAction = (action) => {
    if (!board) return;

    switch(action) {
      case 'rename':
        return handleRenameBoard();
      case 'duplicate':
        return handleDuplicateBoard();
      case 'delete':
        return handleDeleteBoard();
      case 'cancelDelete':
        return handleCancelDelete();
      case 'leave':
        return handleLeaveBoard();
      default:
        console.warn('Unknown action:', action);
    }
  };

  const handleWorkforceToggle = async (checked) => {
    if (!board) return;

    try {
      // Prepare the payload as per the reference implementation
      const payload = JSON.stringify({ value: checked.toString() });

      // Make the API call to update workforce active state
      await updateWFBoardActiveState(board.id, payload);

      // Update the store record after successful API call
      const record = boardManager.boardNameStore.getById(board.id);
      if (record) {
        record.isWorkforceActive = checked;
        // Trigger store update event to notify all listeners
        boardManager.boardNameStore.trigger('update');
      }

      showToast({ msg: "Workforce status updated successfully!", type: "success" });
    } catch (error) {
      showToast({ msg: error?.response?.data || serverErrorMsg(), type: "error" });
    }
  };

  const handleWorkforceChange = async (boardId, checked, boardList = null) => {
    if (!boardId || !boardList) {
      console.warn('Invalid parameters for handleWorkforceChange:', { boardId, checked });
      return;
    }

    const board = boardList.find(b => b.id === boardId);

    if (board) {
      const { handleWorkforceToggle } = useBoardCardActions(board);
      try {
        await handleWorkforceToggle(checked);
      } catch (error) {
        console.warn('Workforce toggle failed for board:', boardId, error);
      }
    } else {
      console.warn('Board not found for workforce change:', boardId);
    }
  };

  const isBoardSelected = (boardId) => {
    return selectedBoard?.id === boardId;
  };

  return {
    handleOpenWorkspace,
    handleMenuAction,
    handleWorkforceToggle,
    handleRenameBoard,
    handleDuplicateBoard,
    handleDeleteBoard,
    handleCancelDelete,
    handleLeaveBoard,
    handleWorkforceChange,
    menuPermissions,
    isBoardSelected
  };
};
